import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAll';
import { timeAgo } from '../lib/timeAgo';
import { useToast } from '../lib/useToast';
import { useEscToClose } from '../lib/useEscToClose';
import { ExportButton } from '../components/ExportButton';
import { UserPostsModal, type UserPostsUser } from '../components/UserPostsModal';

type ReportFilter = 'open' | 'resolved' | 'action_taken' | 'all';

const FILTER_LABELS: Record<ReportFilter, string> = {
  open: 'Open',
  resolved: 'Resolved',
  action_taken: 'Action taken',
  all: 'All',
};

/** Read-only view of a single reported log/post. Columns mirror `logs_select_admin`. */
type ReportedLog = {
  id: string;
  user_id: string | null;
  venue_name: string | null;
  notes: string | null;
  mood_tag: string | null;
  created_at: string;
  flash_url: string | null;
  deleted_at: string | null;
  retired_at: string | null;
};

const REPORTED_LOG_SELECT =
  'id, user_id, venue_name, notes, mood_tag, created_at, flash_url, deleted_at, retired_at';

type ProfileRef = { username: string | null } | { username: string | null }[] | null;

type ReportRow = {
  id: string;
  reporter_id: string | null;
  reported_user_id: string | null;
  reported_log_id?: string | null;
  reason: string | null;
  details: string | null;
  created_at: string;
  status: string | null;
  resolved_at: string | null;
  reporter: ProfileRef;
  reported_user: ProfileRef;
};

type ReportFieldSupport = { logId: boolean };

/**
 * Prod `user_reports` columns: id, reporter_id, reported_user_id, reason, details,
 * created_at, status (default 'open'), resolved_at. The app also attempts to insert
 * `reported_log_id` for post reports (components/FeedPostOverflowMenu.tsx) — that column
 * may or may not exist in prod, so the select degrades gracefully.
 */
const REPORT_SELECT_WITH_LOG =
  'id, reporter_id, reported_user_id, reported_log_id, reason, details, created_at, status, resolved_at, ' +
  'reporter:profiles!user_reports_reporter_id_fkey(username), ' +
  'reported_user:profiles!user_reports_reported_user_id_fkey(username)';

const REPORT_SELECT_NO_LOG =
  'id, reporter_id, reported_user_id, reason, details, created_at, status, resolved_at, ' +
  'reporter:profiles!user_reports_reporter_id_fkey(username), ' +
  'reported_user:profiles!user_reports_reported_user_id_fkey(username)';

function usernameFromRef(p: ProfileRef): string {
  if (!p) return '—';
  const row = Array.isArray(p) ? p[0] : p;
  return row?.username?.trim() || '—';
}

function rawUsernameFromRef(p: ProfileRef): string | null {
  if (!p) return null;
  const row = Array.isArray(p) ? p[0] : p;
  return row?.username?.trim() || null;
}

function isOpen(r: ReportRow): boolean {
  return r.status == null || r.status === 'open';
}

function isActionTaken(r: ReportRow): boolean {
  return r.status === 'action_taken';
}

function statusLabel(r: ReportRow): string {
  if (isOpen(r)) return 'Open';
  if (isActionTaken(r)) return 'Action taken';
  return r.status ?? 'resolved';
}

function statusToneClass(r: ReportRow): string {
  if (isOpen(r)) return 'text-accent';
  if (isActionTaken(r)) return 'text-amber-300';
  return 'text-mutedDim';
}

function sortReports(rows: ReportRow[]): ReportRow[] {
  return [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function ReportsScreen() {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [fieldSupport, setFieldSupport] = useState<ReportFieldSupport>({ logId: false });
  const [filter, setFilter] = useState<ReportFilter>('open');
  const [selected, setSelected] = useState<ReportRow | null>(null);
  const { showError, showSuccess, toastNode } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [postsUser, setPostsUser] = useState<UserPostsUser | null>(null);
  const [postsLoadingId, setPostsLoadingId] = useState<string | null>(null);
  const [reportedLog, setReportedLog] = useState<{
    loading: boolean;
    error: string | null;
    row: ReportedLog | null;
  }>({ loading: false, error: null, row: null });

  // Esc closes the topmost overlay (posts modal first, then the report modal).
  useEscToClose(Boolean(postsUser), () => setPostsUser(null));
  useEscToClose(Boolean(selected) && !postsUser, () => setSelected(null));

  const loadReports = useCallback(async () => {
    setLoading(true);
    setReportsError(null);
    try {
      let rows: ReportRow[];
      let support: ReportFieldSupport = { logId: true };
      try {
        rows = await fetchAllRows<ReportRow>((from, to) =>
          supabase
            .from('user_reports')
            .select(REPORT_SELECT_WITH_LOG)
            .order('created_at', { ascending: false })
            .range(from, to) as unknown as Promise<{ data: ReportRow[] | null; error: { message: string } | null }>,
        );
      } catch {
        rows = await fetchAllRows<ReportRow>((from, to) =>
          supabase
            .from('user_reports')
            .select(REPORT_SELECT_NO_LOG)
            .order('created_at', { ascending: false })
            .range(from, to) as unknown as Promise<{ data: ReportRow[] | null; error: { message: string } | null }>,
        );
        support = { logId: false };
      }
      setFieldSupport(support);
      setReports(sortReports(rows));
    } catch (e: unknown) {
      setReportsError(e instanceof Error ? e.message : 'Failed to load reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  // Fetch the single reported log/post (read-only) when a report with a log id is open.
  const selectedId = selected?.id ?? null;
  const selectedLogId = fieldSupport.logId ? selected?.reported_log_id ?? null : null;
  useEffect(() => {
    if (!selectedLogId) {
      setReportedLog({ loading: false, error: null, row: null });
      return;
    }
    let cancelled = false;
    setReportedLog({ loading: true, error: null, row: null });
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('logs')
          .select(REPORTED_LOG_SELECT)
          .eq('id', selectedLogId)
          .maybeSingle();
        if (error) throw error;
        if (!cancelled) setReportedLog({ loading: false, error: null, row: (data as ReportedLog | null) });
      } catch (e: unknown) {
        if (!cancelled) {
          setReportedLog({
            loading: false,
            error: e instanceof Error ? e.message : 'Failed to load post',
            row: null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, selectedLogId]);

  const filteredReports = useMemo(() => {
    if (filter === 'open') return reports.filter(isOpen);
    if (filter === 'resolved') return reports.filter((r) => !isOpen(r) && !isActionTaken(r));
    if (filter === 'action_taken') return reports.filter(isActionTaken);
    return reports;
  }, [reports, filter]);

  // Repeat-offender map: how many OPEN reports target each reported user (client-side).
  const openReportsByUser = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of reports) {
      if (r.reported_user_id && isOpen(r)) {
        m.set(r.reported_user_id, (m.get(r.reported_user_id) ?? 0) + 1);
      }
    }
    return m;
  }, [reports]);

  function repeatCount(r: ReportRow): number {
    return r.reported_user_id ? openReportsByUser.get(r.reported_user_id) ?? 0 : 0;
  }

  function patchReportRow(id: string, patch: Partial<ReportRow>) {
    setReports((prev) => sortReports(prev.map((x) => (x.id === id ? { ...x, ...patch } : x))));
    setSelected((cur) => (cur?.id === id ? { ...cur, ...patch } : cur));
  }

  async function updateReportRow(id: string, patch: Record<string, unknown>, localPatch: Partial<ReportRow>) {
    setBusyId(id);
    try {
      const { error } = await supabase.from('user_reports').update(patch).eq('id', id);
      if (error) throw error;
      patchReportRow(id, localPatch);
      showSuccess(localPatch.status === 'resolved' ? 'Report resolved' : 'Report reopened');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  }

  async function markResolved(r: ReportRow) {
    const now = new Date().toISOString();
    await updateReportRow(
      r.id,
      { status: 'resolved', resolved_at: now },
      { status: 'resolved', resolved_at: now },
    );
  }

  async function reopen(r: ReportRow) {
    await updateReportRow(
      r.id,
      { status: 'open', resolved_at: null },
      { status: 'open', resolved_at: null },
    );
  }

  // Defensive: status='action_taken' is permitted by the CHECK + admin update policy, but if the
  // value is ever rejected we surface the error rather than break the page or mutate local state.
  async function markActionTaken(r: ReportRow) {
    const now = new Date().toISOString();
    setBusyId(r.id);
    try {
      const { error } = await supabase
        .from('user_reports')
        .update({ status: 'action_taken', resolved_at: now })
        .eq('id', r.id);
      if (error) throw error;
      patchReportRow(r.id, { status: 'action_taken', resolved_at: now });
      showSuccess('Marked action taken');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  }

  // Open the existing UserPostsModal for the reported account (read-only drill-in).
  async function openReportedUserPosts(r: ReportRow) {
    if (!r.reported_user_id) return;
    const userId = r.reported_user_id;
    setPostsLoadingId(r.id);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, created_at, is_admin, avatar_url')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      const prof = (data ?? null) as Partial<UserPostsUser> | null;
      setPostsUser({
        id: userId,
        username: prof?.username ?? rawUsernameFromRef(r.reported_user),
        full_name: prof?.full_name ?? null,
        created_at: prof?.created_at ?? r.created_at,
        is_admin: prof?.is_admin ?? null,
        avatar_url: prof?.avatar_url ?? null,
      });
    } catch {
      // Graceful fallback — still open the modal with what we already know.
      setPostsUser({
        id: userId,
        username: rawUsernameFromRef(r.reported_user),
        full_name: null,
        created_at: r.created_at,
        is_admin: null,
        avatar_url: null,
      });
    } finally {
      setPostsLoadingId(null);
    }
  }

  function targetLabel(r: ReportRow): string {
    if (r.reported_user_id) return `User @${usernameFromRef(r.reported_user)}`;
    if (fieldSupport.logId && r.reported_log_id) return `Post ${r.reported_log_id.slice(0, 8)}`;
    return '—';
  }

  return (
    <div className="admin-page max-w-4xl mx-auto">
      <PageHeader title="Reports" subtitle="User-submitted reports of accounts and posts from the main app" />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div
          className="inline-flex rounded-lg border border-border bg-surface p-0.5"
          role="group"
          aria-label="Filter reports"
        >
          {(['open', 'resolved', 'action_taken', 'all'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === key ? 'bg-accent/25 text-white' : 'text-muted hover:text-white'
              }`}
            >
              {FILTER_LABELS[key]}
            </button>
          ))}
        </div>

        <ExportButton
          rows={filteredReports}
          filename="reports"
          columns={[
            { header: 'id', get: (r) => r.id },
            {
              header: 'target',
              get: (r) =>
                r.reported_user_id
                  ? `@${usernameFromRef(r.reported_user)}`
                  : fieldSupport.logId && r.reported_log_id
                    ? `post:${r.reported_log_id}`
                    : '',
            },
            { header: 'reporter', get: (r) => usernameFromRef(r.reporter) },
            { header: 'reason', get: (r) => r.reason ?? '' },
            { header: 'details', get: (r) => r.details ?? '' },
            { header: 'status', get: (r) => statusLabel(r) },
            { header: 'created_at', get: (r) => r.created_at },
            { header: 'resolved_at', get: (r) => r.resolved_at ?? '' },
          ]}
        />
      </div>

      {reportsError ? (
        <ErrorBanner className="mb-4">{reportsError}</ErrorBanner>
      ) : loading ? (
        <SkeletonRows rows={7} className="py-4" />
      ) : (
        <ul className="rounded-xl border border-border bg-surface overflow-hidden">
          {filteredReports.length === 0 ? (
            <li className="px-5 py-10 text-center text-muted text-sm">
              {reports.length === 0 ? 'No reports' : 'No reports match this filter'}
            </li>
          ) : (
            filteredReports.map((r, i) => {
              const busy = busyId === r.id;
              const open = isOpen(r);
              return (
                <li
                  key={r.id}
                  className={`border-b border-border/50 last:border-b-0 ${
                    i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'
                  }`}
                >
                  <div className="flex items-start gap-3 px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      className="min-w-0 flex-1 text-left flex items-start gap-4 hover:opacity-95 transition-opacity"
                    >
                      <span
                        className={`mt-2 h-2 w-2 rounded-full shrink-0 ${open ? 'bg-accent' : 'bg-border'}`}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span className="admin-label-primary">{targetLabel(r)}</span>
                          {repeatCount(r) >= 2 ? (
                            <span className="rounded-md border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-300">
                              {repeatCount(r)}x reported
                            </span>
                          ) : null}
                          <span className="text-xs text-violet-300/90 truncate max-w-[220px]">
                            from @{usernameFromRef(r.reporter)}
                          </span>
                          <span className="text-mutedDim text-xs">{timeAgo(r.created_at)}</span>
                          <span
                            className={`text-[10px] uppercase tracking-wider ${statusToneClass(r)}`}
                          >
                            {statusLabel(r)}
                          </span>
                        </div>
                        <p className="text-sm text-muted mt-1.5 leading-relaxed truncate">
                          {r.reason?.trim() || '(no reason)'}
                          {r.details?.trim() ? ` — ${r.details.trim()}` : ''}
                        </p>
                      </div>
                    </button>
                    <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:items-center">
                      {open ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void markResolved(r)}
                            className="rounded-md border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted hover:text-white disabled:opacity-50"
                          >
                            Resolve
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void markActionTaken(r)}
                            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
                          >
                            Action taken
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void reopen(r)}
                          className="rounded-md border border-border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted hover:text-white disabled:opacity-50"
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}

      {selected ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 p-4"
          role="presentation"
          onClick={() => setSelected(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-surface p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-white tracking-tight">Report</h3>
                  {repeatCount(selected) >= 2 ? (
                    <span className="rounded-md border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-300">
                      {repeatCount(selected)}x reported
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-mutedDim mt-1.5">
                  {targetLabel(selected)} · {timeAgo(selected.created_at)}
                </p>
                <p className="text-sm text-violet-300/95 mt-2 break-all">
                  Reported by @{usernameFromRef(selected.reporter)}
                </p>
                {selected.reported_user_id ? (
                  <button
                    type="button"
                    disabled={postsLoadingId === selected.id}
                    onClick={() => void openReportedUserPosts(selected)}
                    className="mt-3 rounded-md border border-border bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-muted transition-colors hover:bg-white/[0.07] hover:text-white disabled:opacity-50"
                  >
                    {postsLoadingId === selected.id ? 'Opening…' : "View user's posts"}
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-muted hover:text-white text-sm px-2 py-1 -mr-1 rounded-md hover:bg-white/5"
              >
                Close
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim mb-1">Reason</p>
                <p className="text-sm text-white/95">{selected.reason?.trim() || '—'}</p>
              </div>
              {selected.details?.trim() ? (
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim mb-1">Details</p>
                  <p className="text-sm text-white/95 whitespace-pre-wrap leading-relaxed">
                    {selected.details.trim()}
                  </p>
                </div>
              ) : null}
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim mb-1">Status</p>
                <p className="text-sm text-white/95">
                  {statusLabel(selected)}
                  {selected.resolved_at
                    ? ` · ${isActionTaken(selected) ? 'updated' : 'resolved'} ${timeAgo(selected.resolved_at)}`
                    : ''}
                </p>
              </div>

              {fieldSupport.logId && selected.reported_log_id ? (
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim mb-1">
                    Reported post
                  </p>
                  {reportedLog.loading ? (
                    <p className="text-sm text-muted">Loading post…</p>
                  ) : reportedLog.error ? (
                    <p className="text-sm text-red-300">{reportedLog.error}</p>
                  ) : reportedLog.row ? (
                    <div className="rounded-lg border border-border bg-rowAlt p-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="admin-label-primary text-sm truncate">
                            {reportedLog.row.venue_name?.trim() || 'Untitled venue'}
                          </div>
                          <div className="text-xs text-mutedDim">{timeAgo(reportedLog.row.created_at)}</div>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-1.5">
                          {reportedLog.row.retired_at ? (
                            <span className="rounded-md border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
                              Retired
                            </span>
                          ) : null}
                          {reportedLog.row.deleted_at ? (
                            <span className="rounded-md border border-red-500/40 bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-200">
                              Hidden
                            </span>
                          ) : null}
                          {reportedLog.row.mood_tag?.trim() ? (
                            <span className="rounded-md border border-violet-500/40 bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-200">
                              {reportedLog.row.mood_tag}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {reportedLog.row.flash_url?.trim() ? (
                        <div className="h-32 w-full overflow-hidden rounded-lg bg-border">
                          <img
                            src={reportedLog.row.flash_url}
                            alt=""
                            referrerPolicy="no-referrer"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : null}
                      {reportedLog.row.notes?.trim() ? (
                        <p className="text-sm text-white/95 whitespace-pre-wrap leading-relaxed">
                          {reportedLog.row.notes.trim()}
                        </p>
                      ) : (
                        <p className="text-sm text-mutedDim">(no caption)</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-mutedDim">Post not found (it may have been deleted).</p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              {isOpen(selected) ? (
                <>
                  <button
                    type="button"
                    disabled={busyId === selected.id}
                    onClick={() => void markActionTaken(selected)}
                    className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
                  >
                    Action taken
                  </button>
                  <button
                    type="button"
                    disabled={busyId === selected.id}
                    onClick={() => void markResolved(selected)}
                    className="admin-btn admin-btn-primary"
                  >
                    Mark resolved
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={busyId === selected.id}
                  onClick={() => void reopen(selected)}
                  className="admin-btn admin-btn-secondary disabled:opacity-50"
                >
                  Reopen
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {postsUser ? <UserPostsModal user={postsUser} onClose={() => setPostsUser(null)} /> : null}

      {toastNode}
    </div>
  );
}
